import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Input,
    InputAdornment,
    FormControl,
    FormHelperText,
    Container,
    Paper,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import { useUserContext } from "./context/UserContext"; 
import TemplatePreview from "./TemplatePreview";
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase.config';
import { useNavigate } from "react-router-dom";

const ALL_CURRENCIES = [
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
];

const formatDateDDMMYYYY = (isoDateString) => {
  const date = new Date(isoDateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const QuotationTemplates = () => {
    const navigate = useNavigate(); // Initialize navigate hook
    const context = useUserContext();
    const {
        userProfile,
        isAdmin,
        adminSelectedQuotationTemplate, setAdminSelectedQuotationTemplate,
        quotationLogo, setQuotationLogo,
        quotationBanner, setQuotationBanner,
        quotationBusinessName, setQuotationBusinessName,
        quotationBusinessAddress, setQuotationBusinessAddress,
        quotationValidityDays, setQuotationValidityDays,
        currency, setCurrency,
        quotationDefaultAdditionalNotes, setQuotationDefaultAdditionalNotes,
        quotationDefaultTermsAndConditions, setQuotationDefaultTermsAndConditions,
        quotationDefaultAuthorizedSignature, setQuotationDefaultAuthorizedSignature,
        quotationDefaultAuthorizedSignatureName, setQuotationDefaultAuthorizedSignatureName,
        quotationFooter, setQuotationFooter,
        quotationIdPrefix, setQuotationIdPrefix,
        quotationPrefixSetAt, setQuotationPrefixSetAt,
        quotationCustomCounter, setQuotationCustomCounter,
        quotationCounterSetAt, setQuotationCounterSetAt,
    } = context;

    const [selectedTemplate, setSelectedTemplate] = useState(adminSelectedQuotationTemplate);
    const [showPreview, setShowPreview] = useState(false);
    const [newPrefix, setNewPrefix] = useState(quotationIdPrefix || '');
    const [newCustomCounter, setNewCustomCounter] = useState('');
    const [counterValue, setCounterValue] = useState(null);
    const [counterLoading, setCounterLoading] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [resetting, setResetting] = useState(false);
    
    const currencyOptions = useMemo(() => {
        const inr = ALL_CURRENCIES.find(c => c.code === 'INR');
        const usd = ALL_CURRENCIES.find(c => c.code === 'USD');
        const others = ALL_CURRENCIES.filter(c => c.code !== 'INR' && c.code !== 'USD')
            .sort((a, b) => a.name.localeCompare(b.name));
        const sortedCurrencies = [inr, usd, ...others].filter(Boolean);
        return sortedCurrencies.map(c => ({ 
            value: c.code, 
            label: `${c.name} (${c.symbol})`, 
            ...c 
        }));
    }, []);

    const handleCurrencyChange = (selectedOption) => {
        setCurrency({ 
            code: selectedOption.value, 
            symbol: selectedOption.symbol, 
            name: selectedOption.name 
        });
    };

    const handleTemplateSelection = (template) => {
        setSelectedTemplate(template);
        setShowPreview(true);
    };

    const handleConfirmTemplate = async () => {
        if (isAdmin) {
            try {
                // Save ALL quotation settings to Firestore if tenantId exists
                if (userProfile && userProfile.tenantId) {
                    const settingsRef = doc(db, `tenants/${userProfile.tenantId}/settings`, 'templates');
                    const quotationSettings = {
                        quotationTemplate: selectedTemplate,
                        quotationLogo: quotationLogo,
                        quotationBanner: quotationBanner,
                        quotationBusinessName: quotationBusinessName,
                        quotationBusinessAddress: quotationBusinessAddress,
                        quotationFooter: quotationFooter,
                        quotationDefaultAdditionalNotes: quotationDefaultAdditionalNotes,
                        quotationDefaultTermsAndConditions: quotationDefaultTermsAndConditions,
                        quotationDefaultAuthorizedSignature: quotationDefaultAuthorizedSignature,
                        quotationDefaultAuthorizedSignatureName: quotationDefaultAuthorizedSignatureName,
                        currency: currency,
                        quotationIdPrefix: quotationIdPrefix,
                        quotationCustomCounter: quotationCustomCounter,
                        quotationValidityDays: quotationValidityDays,
                    };
                    await setDoc(settingsRef, quotationSettings, { merge: true });
                    console.log(`All quotation settings saved to Firestore for tenant ${userProfile.tenantId}`);
                }
                
                // Update local state
                setAdminSelectedQuotationTemplate(selectedTemplate);
                alert(`Template '${selectedTemplate}' has been confirmed and saved!`);
                navigate(selectedTemplate);
            } catch (error) {
                console.error("Error saving template to Firestore:", error);
                alert("Template saved locally but could not sync to cloud. Please try again.");
                // Still update local state as fallback
                setAdminSelectedQuotationTemplate(selectedTemplate);
                navigate(selectedTemplate);
            }
        }
    };

    // Fetch current quotation counter from Firestore (quotations/quotationcount)
    useEffect(() => {
        let mounted = true;
        const fetchCounter = async () => {
            setCounterLoading(true);
            try {
                const ref = doc(db, 'quotations', 'quotationcount');
                const snap = await getDoc(ref);
                if (!mounted) return;
                if (snap.exists()) {
                    const data = snap.data();
                    setCounterValue(typeof data.quotationnumber === 'number' ? data.quotationnumber : 0);
                } else {
                    // not present yet
                    setCounterValue(0);
                }
            } catch (err) {
                console.error('Error fetching quotation counter:', err);
                setCounterValue(null);
            } finally {
                setCounterLoading(false);
            }
        };
        fetchCounter();
        return () => { mounted = false; };
    }, []);

    const handleLogoUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 150 * 1024) {
                alert('Logo image size must be below 150KB.');
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                setQuotationLogo(e.target.result);
                setQuotationBanner('');
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBannerUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 150 * 1024) {
                alert('Banner image size must be below 150KB.');
                return;
            }
            const reader = new FileReader();
            reader.onload = (e) => {
                setQuotationBanner(e.target.result);
                setQuotationLogo('');
            };
            reader.readAsDataURL(file);
        }
    };
    
    const handleDefaultSignatureUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
            alert('Only JPEG, PNG, or JPG files are allowed.');
            return;
        }

        if (file.size > 150 * 1024) {
            alert('Signature image size must be below 150KB.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                const maxWidth = 400;
                const maxHeight = 200;
                let { width, height } = img;

                if (width > height) {
                    if (width > maxWidth) {
                        height *= maxWidth / width;
                        width = maxWidth;
                    }
                } else {
                    if (height > maxHeight) {
                        width *= maxHeight / height;
                        height = maxHeight;
                    }
                }
                canvas.width = width;
                canvas.height = height;
                ctx.drawImage(img, 0, 0, width, height);
                const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
                const compressedImageData = canvas.toDataURL(outputType, 0.9);
                setQuotationDefaultAuthorizedSignature(compressedImageData);
            };
        };
        reader.readAsDataURL(file);
    };

    if (!isAdmin && !adminSelectedQuotationTemplate) {
        return (
            <Container maxWidth="sm" sx={{ mt: 5, textAlign: 'center' }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Typography variant="h5" gutterBottom>Quotation Template Not Selected</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please contact your administrator to configure and select a quotation template.
                    </Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Configure Quotation Details
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                    <Box>
                        <Typography variant="h6" gutterBottom>Branding & Details</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Upload either a logo or a banner. Uploading one will remove the other.
                        </Typography>

                        <Input type="file" inputProps={{ accept: 'image/png, image/jpeg' }} onChange={handleLogoUpload} style={{ display: 'none' }} id="logo-upload" />
                        <label htmlFor="logo-upload">
                            <Button variant="contained" component="span" sx={{mr: 2, mb: 1}}>Upload Logo</Button>
                        </label>

                        <Input type="file" inputProps={{ accept: 'image/png, image/jpeg' }} onChange={handleBannerUpload} style={{ display: 'none' }} id="banner-upload" />
                        <label htmlFor="banner-upload">
                            <Button variant="contained" component="span" sx={{mb: 1}}>Upload Banner</Button>
                        </label>
                        <FormHelperText>Recommended banner size: 1600x400 pixels. Max 150KB.</FormHelperText>

                        {quotationLogo && (
                            <Box mt={2} p={1} border="1px dashed grey" borderRadius={2} sx={{ position: 'relative', display: 'inline-block' }}>
                                <IconButton size="small" onClick={() => setQuotationLogo('')} sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255,255,255,0.7)' }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                <img src={quotationLogo} alt="Logo" style={{ maxWidth: '200px', maxHeight: '100px', padding: '8px' }} />
                            </Box>
                        )}

                        {quotationBanner && (
                            <Box mt={2} p={1} border="1px dashed grey" borderRadius={2} sx={{ position: 'relative' }}>
                                <IconButton size="small" onClick={() => setQuotationBanner('')} sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.7)' }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="caption" display="block" sx={{ ml: 1, mb: 1 }}>Banner Preview:</Typography>
                                <Box sx={{ width: '100%', height: '150px', overflow: 'hidden', borderRadius: 1 }}>
                                    <img src={quotationBanner} alt="Banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>
                            </Box>
                        )}
                        {quotationBanner && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                A banner has been uploaded — Business Name and Business Address are optional.
                            </Typography>
                        )}
                        <Box mt={2}>
                            <Typography variant="subtitle2">Numbering Preview</Typography>
                            {quotationCustomCounter && (
                              <>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Format: <strong>{(quotationIdPrefix || 'PREX') + '-' + String(parseInt(quotationCustomCounter) || 0).padStart(parseInt(quotationCustomCounter).toString().length, '0')}</strong> up to <strong>{(quotationIdPrefix || 'PREX') + '-' + String(Math.pow(10, parseInt(quotationCustomCounter).toString().length) - 1).padStart(parseInt(quotationCustomCounter).toString().length, '0')}</strong>
                                </Typography>
                              </>
                            )}
                            {!quotationCustomCounter && (
                              <>
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                    Format: <strong>{(quotationIdPrefix || 'PREX') + '-00000'}</strong> up to <strong>{(quotationIdPrefix || 'PREX') + '-99999'}</strong>
                                </Typography>
                              </>
                            )}
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Current next number: {counterLoading ? 'Loading...' : (counterValue === null ? 'Unavailable' : `${String((counterValue || 0) + 1).padStart(quotationCustomCounter ? parseInt(quotationCustomCounter).toString().length : 5, '0')}`)}
                            </Typography>
                        </Box>
                        <Box mt={3}>
                            <Typography variant="subtitle1">Quotation ID Prefix (Admin)</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                This 4-character alphanumeric prefix will appear on all quotations. It can only be changed by an admin and only once per year after being set.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <TextField
                                    label="Prefix"
                                    value={newPrefix}
                                    onChange={(e) => setNewPrefix(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0,4).toUpperCase())}
                                    size="small"
                                    sx={{ maxWidth: 140 }}
                                />
                                <Box>
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            const now = new Date();
                                            // Save in context
                                            setQuotationIdPrefix(newPrefix);
                                            setQuotationPrefixSetAt(now.toISOString());
                                            alert('Quotation ID prefix has been set. It will be visible to users immediately.');
                                        }}
                                        disabled={!isAdmin || (typeof newPrefix !== 'string') || newPrefix.length !== 4 || (quotationPrefixSetAt && (new Date() - new Date(quotationPrefixSetAt) < 365 * 24 * 60 * 60 * 1000))}
                                    >
                                        Set Prefix
                                    </Button>
                                    &nbsp;
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setNewPrefix('');
                                        }}
                                    >
                                        Clear
                                    </Button>
                                </Box>
                            </Box>
                            {quotationIdPrefix && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                    Current prefix: <strong>{quotationIdPrefix}</strong>
                                    {quotationPrefixSetAt ? ` — Set on ${formatDateDDMMYYYY(quotationPrefixSetAt)}` : ''}
                                </Typography>
                            )}
                            {quotationPrefixSetAt && (new Date() - new Date(quotationPrefixSetAt) < 365 * 24 * 60 * 60 * 1000) && (
                                <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                    Prefix is locked for one year after set. You can change it after {formatDateDDMMYYYY(new Date(new Date(quotationPrefixSetAt).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString())}.
                                </Typography>
                            )}

                            <Box mt={3}>
                                <Typography variant="subtitle2">Set Custom Counter ID Number</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  Set the starting number for quotation counters. Examples: 123456, 98765432, 1010101
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                  <TextField
                                    label="Custom Counter Number"
                                    type="number"
                                    value={newCustomCounter}
                                    onChange={(e) => setNewCustomCounter(e.target.value)}
                                    placeholder="e.g., 123456"
                                    size="small"
                                    sx={{ maxWidth: 200 }}
                                    inputProps={{ min: 0 }}
                                    disabled={quotationCounterSetAt && (new Date() - new Date(quotationCounterSetAt) < 365 * 24 * 60 * 60 * 1000)}
                                  />
                                  <Box>
                                    <Button
                                      variant="contained"
                                      onClick={() => {
                                        const now = new Date();
                                        const nextNum = parseInt(newCustomCounter) + 1;
                                        const digitLength = newCustomCounter.length;
                                        const nextFormatted = String(nextNum).padStart(digitLength, '0');
                                        setQuotationCustomCounter(newCustomCounter);
                                        setQuotationCounterSetAt(now.toISOString());
                                        setNewCustomCounter('');
                                        alert(`Quotation counter has been set to ${newCustomCounter}. Next quotation will be numbered ${nextFormatted}.`);
                                      }}
                                      disabled={!isAdmin || newCustomCounter === '' || (quotationCounterSetAt && (new Date() - new Date(quotationCounterSetAt) < 365 * 24 * 60 * 60 * 1000))}
                                    >
                                      Set Counter
                                    </Button>
                                    &nbsp;
                                    <Button variant="outlined" onClick={() => setNewCustomCounter('')} disabled={quotationCounterSetAt && (new Date() - new Date(quotationCounterSetAt) < 365 * 24 * 60 * 60 * 1000)}>Clear</Button>
                                  </Box>
                                </Box>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                                  Current counter starts at: <strong>{quotationCustomCounter || '0'}</strong>
                                  {quotationCounterSetAt ? ` — Set on ${formatDateDDMMYYYY(quotationCounterSetAt)}` : ''}
                                </Typography>
                                {quotationCounterSetAt && (new Date() - new Date(quotationCounterSetAt) < 365 * 24 * 60 * 60 * 1000) && (
                                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                    Counter is locked for one year after set. You can change it after {formatDateDDMMYYYY(new Date(new Date(quotationCounterSetAt).getTime() + 365 * 24 * 60 * 60 * 1000).toISOString())}.
                                  </Typography>
                                )}
                            </Box>

                        </Box>
                    </Box>

                    <TextField label="Business Name" value={quotationBusinessName || ''} onChange={(e) => setQuotationBusinessName(e.target.value)} fullWidth />
                    <TextField label="Business Address" value={quotationBusinessAddress || ''} onChange={(e) => setQuotationBusinessAddress(e.target.value)} fullWidth multiline rows={3} />
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="h6" gutterBottom>Policies & Defaults</Typography>
                    <TextField label="Quotation Validity Period" type="number" value={quotationValidityDays || 7} onChange={(e) => setQuotationValidityDays(e.target.value)} InputProps={{ endAdornment: <InputAdornment position="end">Days</InputAdornment> }} fullWidth />

                    <FormControl fullWidth>
                        <Typography variant="body1" sx={{ mb: 1, color: 'text.secondary' }}>Default Currency</Typography>
                        <Select
                            options={currencyOptions}
                            value={currencyOptions.find(o => o.value === (currency?.code || 'INR'))}
                            onChange={handleCurrencyChange}
                            placeholder="Search and select a currency..."
                            isSearchable={true}
                        />
                        <FormHelperText>The currency for all quotations.</FormHelperText>
                    </FormControl>

                    <TextField label="Default Additional Notes" value={quotationDefaultAdditionalNotes || ''} onChange={e => setQuotationDefaultAdditionalNotes(e.target.value)} fullWidth multiline rows={4} helperText="These notes will appear on new quotations by default." />
                    <TextField label="Default Terms and Conditions" value={quotationDefaultTermsAndConditions || ''} onChange={e => setQuotationDefaultTermsAndConditions(e.target.value)} fullWidth multiline rows={4} helperText="These terms will appear on new quotations by default." />
                    <Divider sx={{ my: 1 }} />

                    <Typography variant="h6" gutterBottom>Default Authorized Signature</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Signature must be <b>JPEG, PNG, or JPG</b> and <b>below 150KB</b>.</Typography>
                    <Box>
                        <Input type="file" inputProps={{ accept: 'image/jpeg, image/png, image/jpg' }} onChange={handleDefaultSignatureUpload} style={{ display: 'none' }} id="signature-upload" />
                        <label htmlFor="signature-upload">
                            <Button variant="contained" component="span">Upload Signature</Button>
                        </label>
                        {quotationDefaultAuthorizedSignature && (
                            <Box mt={2} p={2} border="1px dashed grey" sx={{ position: 'relative', display: 'inline-block' }}>
                                <IconButton size="small" onClick={() => { setQuotationDefaultAuthorizedSignature(''); setQuotationDefaultAuthorizedSignatureName(''); }} sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255,255,255,0.7)' }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                                <img src={quotationDefaultAuthorizedSignature} alt="Signature" style={{ maxWidth: '300px', maxHeight: '150px' }} />
                            </Box>
                        )}
                        {quotationDefaultAuthorizedSignature && (
                            <TextField label="Name / Title for Signature" value={quotationDefaultAuthorizedSignatureName || ''} onChange={e => setQuotationDefaultAuthorizedSignatureName(e.target.value)} fullWidth sx={{mt: 2}} helperText="e.g., John Doe, Sales Manager" />
                        )}
                    </Box>
                    <Divider sx={{ my: 1 }} />

                    <Typography variant="h6" gutterBottom>Footer</Typography>
                    <TextField label="Quotation Footer (optional)" value={quotationFooter || ""} onChange={e => setQuotationFooter(e.target.value)} fullWidth multiline rows={2} helperText="This footer will appear at the bottom of all quotations." />
                </Box>

                <Divider sx={{ my: 4 }} />

                <Box>
                    <Typography variant="h5" component="h2" gutterBottom>Select Active Template</Typography>
                    <Button variant={selectedTemplate === 'qtemplate' ? "contained" : "outlined"} onClick={() => handleTemplateSelection('qtemplate')}>
                        Template
                    </Button>
                </Box>

                {showPreview && selectedTemplate && (
                    <Box mt={4}>
                        <TemplatePreview 
                            template={selectedTemplate} 
                            onConfirm={handleConfirmTemplate} 
                            isAdmin={isAdmin} 
                        />
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default QuotationTemplates;